import type {AddOptions, Component, Editor, StyleProps} from 'grapesjs';
import juice from 'juice';
import he from 'he';
import {getEmailTextWithCcid} from '@Libraries/email-campaigns-library';
import {EMAIL_MAKER_COMPONENT_TYPES, READ_ONLY_FOOTER_ID} from '@EmailMakerReact/email-maker-react.constants';
import type {EmailMakerExportOption} from '@EmailMakerReact/email-maker-react.types';
import type {EmailPreviewResponse} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getAllComponentsInTree, getMainWrapper} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {getMediaQueries} from '@EmailMakerReact/lib/email-maker-save-payload-library';
import {getCurrentIdEmailTemplate, saveEmailTemplateToDbAndCache} from '@EmailMakerReact/lib/email-maker-storage-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

const GET_SANITIZED_EMAIL_TEMPLATE_HTML_ENDPOINT = 'emailmaker/getSanitizedEmailTemplateHTML';
const GET_BASE_MARKETING_EMAIL_TEMPLATE_HTML_ENDPOINT = 'emailmarketing/getBaseMarketingEmailTemplateHTML';
const GET_EMAIL_TEMPLATE_TEXT_CONTENT_ENDPOINT = 'emailmaker/getEmailTemplateTextContent';
const DO_RECORD_EMAIL_EXPORT_ENDPOINT = 'emailmaker/doRecordEmailExport';
const GET_EMAIL_TEMPLATE_FOOTER_HTML_ENDPOINT = 'emailmaker/getEmailTemplateFooterHTML';

/** Passed to addComponents so the footer is never included in the save payload or the undo count. */
const ADD_FOOTER_WITHOUT_STORE_OPTS: AddOptions & {avoidStore: boolean; noCount: boolean} = {
  avoidStore: true,
  noCount: true,
};

export function getInlinedHtml(editor: Editor): string {
  let html = editor.getHtml();
  const wrapper = editor.getWrapper();

  if (wrapper) {
    const pmwWrapper = wrapper.findType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER);

    if (pmwWrapper.length > 0) {
      html = pmwWrapper[0].toHTML();
    }
  }

  const juicePreppedHtml = preProcessHtmlForJuice(html);
  const tmpl = `${juicePreppedHtml}<style>${editor.getCss() ?? ''}</style>`;
  let juiced: string;
  try {
    juiced = juice(tmpl, {xmlMode: false});
  } catch (e: unknown) {
    logEmailMakerError('getInlinedHtml: juice() failed', e);
    throw new Error('Failed to inline email CSS', {cause: e});
  }
  return postProcessJuicedHtml(juiced);
}

export async function getSanitizedEmailHtml(editor: Editor): Promise<EmailPreviewResponse> {
  return (await window.PMW.writeLocal(GET_SANITIZED_EMAIL_TEMPLATE_HTML_ENDPOINT, {
    htmlContent: getInlinedHtml(editor),
    mediaQueries: getMediaQueries(editor),
  })) as EmailPreviewResponse;
}

export async function getMarketingBaseEmailHtml(editor: Editor): Promise<EmailPreviewResponse> {
  return (await window.PMW.writeLocal(GET_BASE_MARKETING_EMAIL_TEMPLATE_HTML_ENDPOINT, {
    htmlContent: getInlinedHtml(editor),
    mediaQueries: getMediaQueries(editor),
    idEmailTemplate: getCurrentIdEmailTemplate(editor),
  })) as EmailPreviewResponse;
}

/** Serializes the main wrapper with each element stamped with its model id, so the output can be mapped back onto components. */
export function getEditableHtml(editor: Editor): string {
  const mainWrapper = getMainWrapper(editor);
  if (!mainWrapper) {
    return '';
  }

  return mainWrapper.toHTML({
    attributes: withComponentId,
    altQuoteAttr: true,
    keepInlineStyle: true,
  });
}

export function getEditableComponents(editor: Editor, types: string[] = []): Component[] {
  const mainWrapper = getMainWrapper(editor);
  if (!mainWrapper) {
    return [];
  }

  return getAllComponentsInTree(mainWrapper, types);
}

/** Applies the inline styles from an auto-branding HTML snapshot onto the matching components, keyed by id. */
export function replaceHtmlContent(editor: Editor, html: string): void {
  const contentContainer = getMainWrapper(editor);
  const content = getEditableComponents(editor);

  if (!contentContainer || content.length === 0) {
    return;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements = doc.querySelectorAll('[id]');

  const elementMap: Record<string, Element> = {};
  elements.forEach((element) => {
    elementMap[element.id] = element;
  });

  for (const component of content) {
    const element = elementMap[component.getId()];

    if (!element) {
      continue;
    }

    const elementStyles = element.getAttribute('style');
    if (!elementStyles) {
      continue;
    }

    component.setStyle(parseInlineStyles(elementStyles), {noEvent: true});
  }
}

export function getInlineStyledComponentHtml(editor: Editor, component?: Component): string {
  const target = component ?? editor.getSelected();

  if (!target) {
    return '';
  }

  const template = `${target.toHTML()}<style>${editor.getCss()}</style>`;
  try {
    return juice(template, {});
  } catch (e: unknown) {
    logEmailMakerError('getInlineStyledComponentHtml: juice() failed', e);
    throw new Error('Failed to inline component CSS', {cause: e});
  }
}

export async function copyHtmlForMarketingEmail(editor: Editor): Promise<void> {
  await window.PMW.copyEmailHTMLasText('', async (): Promise<EmailPreviewResponse> => {
    await saveEmailTemplateBeforeCopy(editor);
    return getMarketingBaseEmailHtml(editor);
  });
}

async function saveEmailTemplateBeforeCopy(editor: Editor): Promise<void> {
  try {
    await saveEmailTemplateToDbAndCache(editor);
  } catch (e: unknown) {
    logEmailMakerError('copyHtmlForMarketingEmail: save before copy failed', e);
  }
}

export async function copyTextForMarketingEmail(editor: Editor): Promise<void> {
  const idEmailTemplate = getCurrentIdEmailTemplate(editor);
  if (!idEmailTemplate) {
    return;
  }
  const response = (await window.PMW.readLocal(`${GET_EMAIL_TEMPLATE_TEXT_CONTENT_ENDPOINT}/${idEmailTemplate}`)) as {text: string};
  await window.PMW.copyEmailasText(response.text);
}

export async function copyEmailHtmlForExport(editor: Editor): Promise<void> {
  await window.PMW.copyEmailHTML('', () => getSanitizedEmailHtml(editor), 'Editor');
}

export async function copyEmailHtmlAsTextForExport(editor: Editor): Promise<void> {
  await window.PMW.copyEmailHTMLasText('', () => getSanitizedEmailHtml(editor), 'Editor');
}

export async function copyTextJson(editor: Editor): Promise<void> {
  await window.PMW.copyEmailTextAsJson(JSON.stringify(getEmailTextWithCcid(editor)));
}

export async function copyMediaQueriesForMarketingEmail(editor: Editor): Promise<void> {
  await window.PMW.copyEmailHTMLasText(getMediaQueries(editor));
}

export async function downloadHtmlForMarketingEmail(editor: Editor): Promise<void> {
  const data = await getSanitizedEmailHtml(editor);
  window.PMW.downloadEmailHTML(data.html, data.fileName);
}

export function recordEmailExport(editor: Editor, exportOption: EmailMakerExportOption): void {
  void window.PMW.write(window.PMW.util.site_url(DO_RECORD_EMAIL_EXPORT_ENDPOINT), {
    idEmail: getCurrentIdEmailTemplate(editor),
    exportOption,
  });
}

/** Must run after addContentComponent so the footer lands last: the save-payload stripper only pops the footer from the last position. */
export async function addReadOnlyFooter(editor: Editor): Promise<void> {
  if (!editor.DomComponents.getWrapper() || getReadOnlyFooter(editor)) {
    return;
  }

  const response = (await window.PMW.readLocal(GET_EMAIL_TEMPLATE_FOOTER_HTML_ENDPOINT)) as {html: string};

  editor.addComponents(
    {
      type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_READ_ONLY_FOOTER,
      attributes: {
        id: READ_ONLY_FOOTER_ID,
        'data-is-readonly-text': true,
      },
      style: {
        display: 'block',
        'text-align': 'center',
      },
      content: response.html,
    },
    ADD_FOOTER_WITHOUT_STORE_OPTS
  );
}

export function getReadOnlyFooter(editor: Editor): Component | undefined {
  return editor.DomComponents.getWrapper()?.find(`#${READ_ONLY_FOOTER_ID}`)[0];
}

function preProcessHtmlForJuice(html: string): string {
  return he.decode(html);
}

function postProcessJuicedHtml(juiced: string): string {
  return juiced.replace(/<br[^>]*><\/br>/gi, '<br>');
}

function withComponentId(component: Component, attributes: Record<string, unknown>): Record<string, unknown> {
  return {...attributes, id: component.getId()};
}

function parseInlineStyles(elementStyles: string): StyleProps {
  const stylesToApply: StyleProps = {};

  elementStyles.split(';').forEach((style) => {
    if (!style) {
      return;
    }

    const parts = style.split(':');
    if (parts.length < 2) {
      return;
    }

    const key = parts[0].trim();
    const value = parts.slice(1).join(':').trim();
    if (key && value) {
      stylesToApply[key] = value;
    }
  });

  return stylesToApply;
}
